import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { getDefaultRoute } from "@/lib/rbac";
import XLSyncLogo from "@/components/brand/XLSyncLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { isAuthenticated, isLoading, permissions, user } = useAuth();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--xl-bg)]">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--xl-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute(permissions, user?.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Email is required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: trimmed });
      setSent(true);
    } catch (_) {
      // Always show the generic success message — don't reveal whether the email exists.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen flex items-center justify-center bg-[var(--xl-bg)] px-4"
    >
      <div className="w-full max-w-sm">
        <div className="bg-[var(--xl-surf)] rounded-2xl border border-[var(--xl-line)] shadow-[0_8px_30px_rgba(22,32,46,0.06)] overflow-hidden">
          <div
            className="flex flex-col items-center px-4 pt-4 pb-3 border-b border-[var(--xl-line)] bg-[var(--xl-surf)]"
            data-testid="forgot-password-brand"
          >
            <XLSyncLogo size="xl" className="w-full max-w-[280px]" />
            <p className="text-sm text-[var(--xl-mut)] mt-2">
              Reset your password
            </p>
          </div>

          <div className="p-6 space-y-5">
            {sent ? (
              <div
                data-testid="forgot-password-success"
                className="space-y-4 text-center"
              >
                <div className="flex justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" aria-hidden />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[var(--xl-text)]">
                    Check your email
                  </p>
                  <p className="text-sm text-[var(--xl-mut)]">
                    If that email exists, a reset link has been sent. Check your inbox
                    and follow the link to set a new password.
                  </p>
                </div>
                <Link
                  to="/login"
                  data-testid="forgot-password-back-login"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--xl-primary)] hover:underline"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <p className="text-sm text-[var(--xl-mut)]">
                  Enter the email associated with your account and we&apos;ll send you
                  a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs text-[var(--xl-mut)]">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      data-testid="forgot-password-email-input"
                      type="email"
                      autoComplete="email"
                      placeholder="you@hospital.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-[var(--xl-line2)] focus-visible:ring-primary/25"
                      required
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="forgot-password-error"
                      className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2"
                    >
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="forgot-password-submit-btn"
                    disabled={submitting}
                    className="w-full h-11 rounded-full font-semibold"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </Button>
                </form>

                <div className="pt-4 border-t border-[var(--xl-line)] text-center">
                  <Link
                    to="/login"
                    data-testid="forgot-password-back-login"
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--xl-mut)] hover:text-[var(--xl-text)]"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-[var(--xl-mut2)] text-center mt-6 space-x-2">
          <span>XLSync · Clinical imaging intelligence · Admin-invite only</span>
          <span aria-hidden>·</span>
          <Link
            to="/privacy"
            data-testid="forgot-password-privacy-link"
            className="text-[var(--xl-primary)] hover:underline"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
